import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ExpenseStatusInput } from '@armard/shared';
import { Button, Input, Modal } from '../ui';
import { useToastHelpers } from '../ui/Toast';
import { useSession } from '../../hooks/useTenantContext';
import { useTransitionExpenseStatus } from '../../services/api/expenses';
import { apiErrorMessage } from '../../lib/api-error-message';

export function ExpenseRemovalAction({
  id,
  status,
  version,
}: {
  id: string;
  status: ExpenseStatusInput;
  version: number;
}) {
  const { data: session } = useSession();
  const transition = useTransitionExpenseStatus(id);
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (
    session?.activeRole !== 'geschäftsführer' ||
    !['draft', 'neu', 'geprüft', 'rückfrage_offen', 'nicht_relevant'].includes(status)
  )
    return null;

  const label = status === 'draft' ? 'Entwurf verwerfen' : 'Beleg zurückziehen';
  const close = () => {
    if (!transition.isPending) setOpen(false);
  };
  async function confirm() {
    try {
      await transition.mutateAsync({ toStatus: 'zurückgezogen', version, reason: reason.trim() });
      setOpen(false);
      toast.success(status === 'draft' ? 'Entwurf verworfen' : 'Beleg zurückgezogen');
      void navigate({ to: '/expenses' });
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  }
  return (
    <>
      <Button
        variant="danger"
        onClick={() => {
          setReason('');
          setOpen(true);
        }}
      >
        {label}
      </Button>
      <Modal isOpen={open} onClose={close} title={`${label}?`}>
        <p className="mb-4 text-sm text-white/70">
          {status === 'draft' ? 'Der Entwurf' : 'Der Beleg'} wird als „Zurückgezogen“ markiert und
          aus der Standardliste entfernt. Originaldateien und Verlauf bleiben erhalten. Du findest
          ihn weiterhin über den Statusfilter „Zurückgezogen“.
        </p>
        <Input
          label="Begründung"
          value={reason}
          disabled={transition.isPending}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button variant="ghost" disabled={transition.isPending} onClick={close}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            disabled={!reason.trim() || transition.isPending}
            onClick={() => void confirm()}
          >
            {label}
          </Button>
        </div>
      </Modal>
    </>
  );
}
